/**
 * Labstep
 *
 * @module grid/SmartTableProtocolCondition/coldefs/protocol-value
 * @desc Grid SmartTable Column Definitions
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
} from 'ag-grid-community';
import { ResourceItemActionCreateProtocolValueWithProtocolCondition } from 'labstep-web/components/ResourceItem/Action/CreateProtocolValue';
import { ICONS } from 'labstep-web/constants/icons';
import { DataGridCellEditor } from 'labstep-web/core/DataGrid/CellEditor';
import { suppressKeyboardEventClearCells } from 'labstep-web/core/DataGrid/CellEditor/utils';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import type {
  CellEditorParams,
  CellRendererParams,
  HeaderComponentParamsProps,
} from 'labstep-web/core/DataGrid/types';
import { stopEditingAndFocus } from 'labstep-web/core/DataGrid/utils';
import type { IconProps } from 'labstep-web/core/Icon/types';
import type { SmartTableColDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/types';
import { getProtocolConditionProps } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/utils';
import { ResourceItemCellRenderer } from 'labstep-web/grid/SmartTableProtocolCondition/components/CellRenderer/ResourceItemCellRenderer';
import { GridSmartTableColumnHeader } from 'labstep-web/grid/SmartTableProtocolCondition/components/ColumnHeader';
import { GridSmartTableProtocolConditionService } from 'labstep-web/grid/SmartTableProtocolCondition/services/grid-smart-table-protocol-condition.service';
import { AmountUpdate } from 'labstep-web/models/amount-update.model';
import type { Experiment } from 'labstep-web/models/experiment.model';
import { Protocol } from 'labstep-web/models/protocol.model';
import { ProtocolCondition } from 'labstep-web/models/protocol-condition.model';
import { ProtocolValue } from 'labstep-web/models/protocol-value.model';
import { ResourceItem } from 'labstep-web/models/resource-item.model';
import { evaluateProtocolValue } from 'labstep-web/services/protocol-value.service';

export const getProtocolValueResourceItemColumnDef = (
  experimentOrProtocol: Experiment | Protocol,
  name: string,
): SmartTableColDef => ({
  colId: `protocol_value:${name}:resource_item`,
  sortable: true,
  headerName: 'Item',
  headerComponent: GridSmartTableColumnHeader,
  headerComponentParams: (params: HeaderComponentParamsProps) => {
    const constant =
      GridSmartTableProtocolConditionService.getProtocolValueConstant(
        experimentOrProtocol,
        name,
      );
    return {
      toolPanelColumnName: `Item - ${name}`,
      icon: ICONS.resource_item.primary,
      secondaryHeader: name,
      bulkCreateResourceItemEnabled:
        !!GridSmartTableProtocolConditionService.getProtocolConditionVariableTemplate(
          experimentOrProtocol,
          ProtocolValue.entityName,
          name,
        ),
      experimentId: experimentOrProtocol.id,
      protocolValueName: name,
      variableTemplateGuid:
        GridSmartTableProtocolConditionService.getProtocolConditionVariableTemplate(
          experimentOrProtocol,
          ProtocolValue.entityName,
          name,
        )?.guid,
      colDefId: params?.column?.getColId(),
      isConstant: !!constant,
      experimentOrProtocol,
      protocolValue:
        GridSmartTableProtocolConditionService.getProtocolValue(
          experimentOrProtocol,
          name,
        ),
    };
  },
  editable: (params: EditableCallbackParams) =>
    GridSmartTableProtocolConditionService.getProtocolValueEditable(
      params,
      experimentOrProtocol,
      name,
    ),
  valueGetter: (params: ValueGetterParams) => {
    if (!params.node) {
      return '';
    }
    const protocolValue =
      GridSmartTableProtocolConditionService.getProtocolConditionProtocolValue(
        params.node.data,
        experimentOrProtocol,
        name,
      );

    return protocolValue?.resourceItem?.guid;
  },
  cellRenderer: (params: CellRendererParams<ProtocolCondition>) => {
    if (!params.node) {
      return null;
    }
    GridSmartTableProtocolConditionService.getProtocolValueConstant(
      experimentOrProtocol,
      name,
    );
    const variableTemplate =
      GridSmartTableProtocolConditionService.getProtocolConditionVariableTemplate(
        experimentOrProtocol,
        ProtocolValue.entityName,
        name,
      );

    const protocolValueConstant: any =
      GridSmartTableProtocolConditionService.getProtocolValueConstant(
        experimentOrProtocol,
        name,
      );

    if (!variableTemplate && !protocolValueConstant) {
      return null;
    }

    if (experimentOrProtocol instanceof Protocol) {
      return variableTemplate && variableTemplate.is_input ? (
        <span>
          <DataGridPlaceholder
            children={'To be selected'}
            params={params}
            editable={GridSmartTableProtocolConditionService.getProtocolValueEditable(
              params,
              experimentOrProtocol,
              name,
            )}
          />
        </span>
      ) : (
        <span>
          <DataGridPlaceholder
            children={'To be created'}
            params={params}
            editable={GridSmartTableProtocolConditionService.getProtocolValueEditable(
              params,
              experimentOrProtocol,
              name,
            )}
          />
        </span>
      );
    }

    const protocolValue =
      GridSmartTableProtocolConditionService.getProtocolConditionProtocolValue(
        params.node.data,
        experimentOrProtocol,
        name,
      ) || protocolValueConstant;

    if (!protocolValue) {
      const body: { [key: string]: any } = {
        [`${experimentOrProtocol.entityName}_id`]:
          experimentOrProtocol.id,
        protocol_condition_id: params.node.data.guid,
      };

      if (variableTemplate) {
        body.variable_template_id = variableTemplate.guid;
      }

      return variableTemplate && variableTemplate.is_input ? (
        <DataGridPlaceholder
          children={'Select'}
          params={params}
          editable={GridSmartTableProtocolConditionService.getProtocolValueEditable(
            params,
            experimentOrProtocol,
            name,
          )}
        />
      ) : (
        <DataGridPlaceholder
          children={'Create'}
          params={params}
          editable={GridSmartTableProtocolConditionService.getProtocolValueEditable(
            params,
            experimentOrProtocol,
            name,
          )}
        />
      );
    }

    const { canUpdate, content, iconName, iconColor } =
      evaluateProtocolValue(protocolValue);
    const actionIconProps: IconProps = {
      name: iconName,
      color: iconColor,
      popup: {
        content: content,
        size: 'small',
      },
      onClick: canUpdate
        ? () => {
            return GridSmartTableProtocolConditionService.createEntity(
              params,
              AmountUpdate.entityName,
              {
                protocol_value_id: protocolValue.guid,
              },
            );
          }
        : undefined,
    };

    const protocolValueResourceItem =
      variableTemplate?.is_input || protocolValueConstant?.is_input
        ? protocolValue.resourceItem
        : protocolValue.resource_item_output;

    if (protocolValueResourceItem) {
      return (
        <ResourceItemCellRenderer
          protocolValue={protocolValue}
          resourceItemId={protocolValueResourceItem.id!}
          actionIconProps={actionIconProps}
        />
      );
    }

    return variableTemplate?.is_input ||
      protocolValueConstant?.is_input ? (
      <span>
        <DataGridPlaceholder
          children={'Select'}
          params={params}
          editable={GridSmartTableProtocolConditionService.getProtocolValueEditable(
            params,
            experimentOrProtocol,
            name,
          )}
        />
      </span>
    ) : (
      <DataGridPlaceholder
        children={'Create'}
        params={params}
        editable={GridSmartTableProtocolConditionService.getProtocolValueEditable(
          params,
          experimentOrProtocol,
          name,
        )}
      />
    );
  },
  cellEditor: (params: any) => {
    if (!params.node) {
      return null;
    }

    const variableTemplate =
      GridSmartTableProtocolConditionService.getProtocolValueVariableTemplate(
        experimentOrProtocol,
        name,
      );

    const protocolValueConstant: ProtocolValue | null =
      GridSmartTableProtocolConditionService.getProtocolValueConstant(
        experimentOrProtocol,
        name,
      );

    const protocolValue: ProtocolValue | null =
      GridSmartTableProtocolConditionService.getProtocolConditionProtocolValue(
        params.node.data,
        experimentOrProtocol,
        name,
      ) || protocolValueConstant;

    const body: { [key: string]: any } = {
      [`${experimentOrProtocol.entityName}_id`]:
        experimentOrProtocol.guid,
      protocol_condition_id: params.node.data.id,
    };

    if (variableTemplate) {
      body.variable_template_guid = variableTemplate.guid;
      body.is_input = !!variableTemplate.is_input;
      body.is_output = !variableTemplate.is_input;
    }

    return variableTemplate && variableTemplate.is_input ? (
      <DataGridCellEditor.SearchSelectModal
        {...params}
        {...params.colDef.cellEditorParams(params)}
      />
    ) : (
      <ResourceItemActionCreateProtocolValueWithProtocolCondition
        modalProps={{
          initialModalState: true,
          onUnmount: () => {
            stopEditingAndFocus(params);
          },
        }}
        protocolValue={protocolValue}
        body={!protocolValue ? body : undefined}
        resource={
          variableTemplate?.resource ||
          protocolValueConstant?.resource ||
          undefined
        }
        resourceTemplate={
          variableTemplate?.resource_template ||
          protocolValueConstant?.resource_template ||
          undefined
        }
        actionComponentProps={{
          disabled:
            !GridSmartTableProtocolConditionService.getProtocolValueEditable(
              params,
              experimentOrProtocol,
              name,
            ),
          type: 'text',
          text: 'Create',
          elementProps: {
            placeholder: true,
            gridCell: true,
          },
        }}
        options={{
          onSuccess: () => {
            if (protocolValueConstant) {
              GridSmartTableProtocolConditionService.refreshGrid(
                params,
              );
            } else {
              GridSmartTableProtocolConditionService.refreshRow(
                params,
              );
            }
          },
        }}
      />
    );
  },
  cellEditorParams: (params: CellEditorParams<ProtocolCondition>) => {
    if (!params.node) {
      return false;
    }

    const protocolValueConstant =
      GridSmartTableProtocolConditionService.getProtocolValueConstant(
        experimentOrProtocol,
        name,
      );

    const protocolValue =
      GridSmartTableProtocolConditionService.getProtocolConditionProtocolValue(
        params.node.data,
        experimentOrProtocol,
        name,
      ) || protocolValueConstant;

    const variableTemplate =
      GridSmartTableProtocolConditionService.getProtocolValueVariableTemplate(
        experimentOrProtocol,
        name,
      );

    const props = getProtocolConditionProps(
      params.node.data,
      protocolValue || variableTemplate || undefined,
    );

    props.onChange = (option): void => {
      if (protocolValue) {
        if (protocolValue.is_input) {
          GridSmartTableProtocolConditionService.updateEntity(
            params,
            protocolValue.entityName,
            protocolValue.idAttr,
            {
              resource_item_id: option.id,
              [`${experimentOrProtocol.entityName}_id`]:
                experimentOrProtocol.guid,
            },
            () => {
              if (protocolValueConstant) {
                GridSmartTableProtocolConditionService.refreshGrid(
                  params,
                );
              } else {
                GridSmartTableProtocolConditionService.refreshRow(
                  params,
                );
              }
            },
          );
        } else {
          GridSmartTableProtocolConditionService.updateEntity(
            params,
            protocolValue.entityName,
            protocolValue.idAttr,
            {
              protocol_value_origin_guid: protocolValue.guid,
            },
            () => {
              if (protocolValueConstant) {
                GridSmartTableProtocolConditionService.refreshGrid(
                  params,
                );
              } else {
                GridSmartTableProtocolConditionService.refreshRow(
                  params,
                );
              }
            },
          );
        }
      } else if (variableTemplate && !protocolValueConstant) {
        const body: { [key: string]: any } = {
          [`${experimentOrProtocol.entityName}_id`]:
            experimentOrProtocol.guid,
          variable_template_guid: variableTemplate?.guid,
          ...(variableTemplate.unit && {
            unit: variableTemplate.unit,
          }),
          ...(variableTemplate.is_input
            ? {
                resource_item_id: option.id,
                [`${experimentOrProtocol.entityName}_id`]:
                  experimentOrProtocol.guid,
              }
            : {
                resource_item_output_id: option.id,
              }),
          is_input: !!variableTemplate.is_input,
          is_output: !variableTemplate.is_input,
          protocol_condition_id: params?.node?.data.id,
        };

        if (variableTemplate.is_input) {
          GridSmartTableProtocolConditionService.createEntity(
            params,
            ProtocolValue.entityName,
            body,
            ProtocolCondition.entityName,
            params?.node?.data.guid,
            () => {
              GridSmartTableProtocolConditionService.refreshRow(
                params,
              );
            },
          );
        } else {
          GridSmartTableProtocolConditionService.createEntity(
            params,
            ProtocolValue.entityName,
            body,
            ProtocolCondition.entityName,
            params?.node?.data.guid,
            () => {
              GridSmartTableProtocolConditionService.refreshRow(
                params,
              );
            },
          );
          // FIXME
          // onSuccess: ({ response }) => {
          //   GridSmartTableProtocolConditionService.updateEntity(
          //     params,
          //     ResourceItem.entityName,
          //     option.id,
          //     {
          //       protocol_value_origin_guid: response?.result,
          //     },
          //   );
          // },
        }
      }
    };
    props.preventDefaultOnChange = true;
    return {
      props,
    };
  },
  valueSetter: (params) => {
    const newValue = params.newValue;
    if (
      !params.node ||
      newValue === '' ||
      params.newValue === params.oldValue
    ) {
      return false;
    }

    const protocolValueConstant =
      GridSmartTableProtocolConditionService.getProtocolValueConstant(
        experimentOrProtocol,
        name,
      );
    const protocolValue =
      GridSmartTableProtocolConditionService.getProtocolConditionProtocolValue(
        params.node.data,
        experimentOrProtocol,
        name,
      ) || protocolValueConstant;

    const variableTemplate =
      GridSmartTableProtocolConditionService.getProtocolValueVariableTemplate(
        experimentOrProtocol,
        name,
      );

    if (protocolValue) {
      if (protocolValue.is_input) {
        if (params.newValue === null) {
          return GridSmartTableProtocolConditionService.updateEntity(
            params,
            ProtocolValue.entityName,
            protocolValue.idAttr,
            {
              resource_item_id: null,
            },
            () => {
              if (protocolValueConstant) {
                GridSmartTableProtocolConditionService.refreshGrid(
                  params,
                );
              } else {
                GridSmartTableProtocolConditionService.refreshRow(
                  params,
                );
              }
            },
          );
        } else {
          return GridSmartTableProtocolConditionService.updateEntity(
            params,
            ProtocolValue.entityName,
            protocolValue.idAttr,
            {
              resource_item_id: params.newValue,
              [`${experimentOrProtocol.entityName}_id`]:
                experimentOrProtocol.guid,
            },
            () => {
              if (protocolValueConstant) {
                GridSmartTableProtocolConditionService.refreshGrid(
                  params,
                );
              } else {
                GridSmartTableProtocolConditionService.refreshRow(
                  params,
                );
              }
            },
          );
        }
      } else if (params.newValue === null) {
        return GridSmartTableProtocolConditionService.updateEntity(
          params,
          ResourceItem.entityName,
          protocolValue.resourceItem!.id,
          {
            protocol_value_origin_guid: null,
          },
          () => {
            if (protocolValueConstant) {
              GridSmartTableProtocolConditionService.refreshGrid(
                params,
              );
            } else {
              GridSmartTableProtocolConditionService.refreshRow(
                params,
              );
            }
          },
        );
      } else {
        return GridSmartTableProtocolConditionService.updateEntity(
          params,
          ResourceItem.entityName,
          params.newValue,
          {
            protocol_value_origin_guid: protocolValue.guid,
          },
          () => {
            if (protocolValueConstant) {
              GridSmartTableProtocolConditionService.refreshGrid(
                params,
              );
            } else {
              GridSmartTableProtocolConditionService.refreshRow(
                params,
              );
            }
          },
        );
      }
    } else if (variableTemplate) {
      const body: { [key: string]: any } = {
        [`${experimentOrProtocol.entityName}_id`]:
          experimentOrProtocol.guid,
        variable_template_guid: variableTemplate?.guid,
        ...(variableTemplate.unit && {
          unit: variableTemplate.unit,
        }),
        ...(variableTemplate.is_input
          ? {
              resource_item_id: newValue,
            }
          : {
              resource_item_output_id: newValue,
            }),
        is_input: !!variableTemplate.is_input,
        is_output: !variableTemplate.is_input,
        protocol_condition_id: params?.node?.data.id,
      };

      if (variableTemplate.is_input) {
        GridSmartTableProtocolConditionService.createEntity(
          params,
          ProtocolValue.entityName,
          body,
          ProtocolCondition.entityName,
          params?.node?.data.guid,
          () => {
            GridSmartTableProtocolConditionService.refreshRow(params);
          },
        );
      } else {
        GridSmartTableProtocolConditionService.createEntity(
          params,
          ProtocolValue.entityName,
          body,
          ProtocolCondition.entityName,
          params?.node?.data.guid,
          () => {
            GridSmartTableProtocolConditionService.refreshRow(params);
          },
        );
        // FIXME
        // onSuccess: ({ response }) => {
        //   setValue(
        //     'protocol_value_origin_guid',
        //     response?.result,
        //     {
        //       entityName: ResourceItem.entityName,
        //       id: newValue,
        //     },
        //     rules.protocol_value.protocol_value_origin_guid,
        //     GridSmartTableProtocolConditionService.getOptions(params, true),
        //   );
        // },
      }
    }

    return false;
  },
  cellStyle: (params) =>
    GridSmartTableProtocolConditionService.getCellStyle(
      params,
      experimentOrProtocol,
      ProtocolValue.entityName,
      name,
    ),
  suppressKeyboardEvent: suppressKeyboardEventClearCells,
  valueClearer: () => null,
});
